import React from 'react';
import { FiBarChart2, FiTrendingUp, FiUsers, FiCheckCircle } from 'react-icons/fi';

const QNavAnalytics: React.FC = () => {
  // Updated adoption metrics
  const metrics = [
    { name: 'Entitled Users', value: '457', change: '', icon: FiBarChart2, color: 'bg-primary-600' },
    { name: 'Paid Users', value: '383', change: '', icon: FiUsers, color: 'bg-success-500' },
    { name: 'Active T4W', value: '336', change: '', icon: FiTrendingUp, color: 'bg-warning-500' },
    { name: 'Paid WoW%', value: '6%', change: '', icon: FiCheckCircle, color: 'bg-accent-500' },
  ];

  // Mock data for weekly adoption trend (for future use)
  /*
  const weeklyAdoption = [
    { week: 'Week 1', users: 124, target: 100 },
    { week: 'Week 2', users: 256, target: 250 },
    { week: 'Week 3', users: 412, target: 400 },
    { week: 'Week 4', users: 568, target: 550 },
    { week: 'Week 5', users: 724, target: 700 },
    { week: 'Week 6', users: 876, target: 850 },
  ];
  */

  // Mock data for department adoption
  const departmentAdoption = [
    { department: 'Engineering', users: 324, target: 350, percentage: 92 },
    { department: 'Product', users: 186, target: 200, percentage: 93 },
    { department: 'Data Science', users: 142, target: 150, percentage: 95 },
    { department: 'DevOps', users: 98, target: 100, percentage: 98 },
    { department: 'QA', users: 126, target: 150, percentage: 84 },
  ];

  return (
    <div className="h-full flex flex-col space-y-6">
      <div className="bg-brand-gradient text-white p-6 rounded-qnav shadow-brand-card">
        <h1 className="text-2xl font-display font-bold mb-2">Adoption Analytics</h1>
        <p className="text-secondary-200">Track your Q Developer adoption journey with key metrics and insights</p>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {metrics.map((metric, index) => (
          <div key={index} className="bg-surface rounded-qnav shadow-brand-card p-4 border border-secondary-200">
            <div className="flex items-center mb-2">
              <div className={`p-2 rounded-full ${metric.color} text-white mr-3`}>
                <metric.icon className="h-5 w-5" />
              </div>
              <span className="text-secondary-600 font-medium">{metric.name}</span>
            </div>
            <div className="flex items-baseline">
              <span className="text-3xl font-bold text-secondary-800">{metric.value}</span>
              <span className={`ml-2 text-sm ${metric.change.startsWith('+') ? 'text-success-500' : 'text-error-500'}`}>
                {metric.change}
              </span>
            </div>
          </div>
        ))}
      </div>

      {/* Adoption Insights */}
      <div className="bg-brand-blue rounded-qnav shadow-brand-card p-6 text-white">
        <h2 className="text-xl font-semibold mb-4">Adoption Insights</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-secondary-800 p-4 rounded-qnav">
            <h3 className="font-medium mb-2">Weekly Growth Rate</h3>
            <div className="text-3xl font-bold text-brand-orange mb-2">+12.4%</div>
            <p className="text-secondary-300 text-sm">
              User adoption is growing faster than projected. Current trajectory suggests we'll exceed target by 15% at
              program completion.
            </p>
            <div className="mt-3">
              <button className="text-sm text-brand-orange hover:underline">View Growth Analysis</button>
            </div>
          </div>
          <div className="bg-secondary-800 p-4 rounded-qnav">
            <h3 className="font-medium mb-2">User Retention</h3>
            <div className="text-3xl font-bold text-brand-orange mb-2">94%</div>
            <p className="text-secondary-300 text-sm">
              94% of users who try Q Developer continue to use it weekly. This is 23% higher than industry average for
              developer tools.
            </p>
            <div className="mt-3">
              <button className="text-sm text-brand-orange hover:underline">View Retention Details</button>
            </div>
          </div>
        </div>
      </div>

      {/* Department Adoption and Program Progress Summary */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Department Adoption */}
        <div className="bg-surface rounded-qnav shadow-brand-card p-6 border border-secondary-200">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold text-secondary-800">Department Adoption</h2>
            <span className="text-xs bg-primary-100 text-primary-700 px-2 py-1 rounded-full">Last 30 days</span>
          </div>
          <div className="space-y-3">
            {departmentAdoption.map((dept, index) => (
              <div key={index} className="space-y-1.5">
                <div className="flex justify-between items-center text-sm">
                  <div className="flex items-center">
                    <div
                      className={`w-2 h-2 rounded-full mr-2 ${
                        dept.percentage >= 90 ? 'bg-green-500' : dept.percentage >= 75 ? 'bg-yellow-500' : 'bg-red-500'
                      }`}
                    ></div>
                    <span className="font-medium text-secondary-800">{dept.department}</span>
                  </div>
                  <span className="font-medium text-secondary-700">
                    {dept.users}/{dept.target} <span className="text-secondary-500">({dept.percentage}%)</span>
                  </span>
                </div>
                <div className="w-full bg-secondary-100 rounded-full h-2.5 overflow-hidden relative">
                  <div
                    className={`h-2.5 rounded-full ${
                      dept.percentage >= 90
                        ? 'bg-gradient-to-r from-green-400 to-green-600'
                        : dept.percentage >= 75
                          ? 'bg-gradient-to-r from-yellow-400 to-yellow-500'
                          : 'bg-gradient-to-r from-red-400 to-red-600'
                    }`}
                    style={{ width: `${dept.percentage}%` }}
                    role="progressbar"
                    aria-valuenow={dept.percentage}
                    aria-valuemin={0}
                    aria-valuemax={100}
                  >
                    {dept.percentage > 50 && (
                      <div className="absolute inset-0 opacity-20 overflow-hidden">
                        <div
                          className="w-full h-full bg-white bg-opacity-30"
                          style={{
                            backgroundImage:
                              'linear-gradient(45deg, rgba(255,255,255,.15) 25%, transparent 25%, transparent 50%, rgba(255,255,255,.15) 50%, rgba(255,255,255,.15) 75%, transparent 75%, transparent)',
                            backgroundSize: '1rem 1rem',
                          }}
                        ></div>
                      </div>
                    )}
                  </div>
                  {dept.percentage >= 95 && (
                    <div className="absolute right-1 top-1/2 transform -translate-y-1/2">
                      <div className="w-1.5 h-1.5 bg-white rounded-full shadow-sm"></div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
          <div className="mt-4 text-center">
            <button className="text-sm text-primary-600 hover:text-primary-800 font-medium hover:underline">
              View Detailed Report
            </button>
          </div>
        </div>

        {/* Program Progress Summary */}
        <div className="bg-surface rounded-qnav shadow-brand-card p-6 border border-secondary-200">
          <h2 className="text-xl font-semibold text-secondary-800 mb-4">Program Progress</h2>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-secondary-700">Overall Completion</span>
              <span className="text-secondary-700 font-medium">65%</span>
            </div>
            <div className="w-full bg-secondary-100 rounded-full h-2.5 overflow-hidden">
              <div
                className="h-2.5 rounded-full bg-gradient-to-r from-primary-500 to-primary-700"
                style={{ width: '65%' }}
              ></div>
            </div>

            <div className="grid grid-cols-3 gap-3 mt-3">
              <div className="bg-surface-blue p-3 rounded-qnav border border-primary-100">
                <h3 className="font-medium text-secondary-800 text-sm">Phase 1</h3>
                <div className="flex items-center mt-1.5">
                  <div className="w-full bg-secondary-100 rounded-full h-2 overflow-hidden">
                    <div
                      className="h-2 rounded-full bg-gradient-to-r from-green-400 to-green-600"
                      style={{ width: '100%' }}
                    ></div>
                  </div>
                  <span className="ml-2 text-xs text-green-600">100%</span>
                </div>
              </div>

              <div className="bg-surface-blue p-3 rounded-qnav border border-primary-100">
                <h3 className="font-medium text-secondary-800 text-sm">Phase 2</h3>
                <div className="flex items-center mt-1.5">
                  <div className="w-full bg-secondary-100 rounded-full h-2 overflow-hidden">
                    <div
                      className="h-2 rounded-full bg-gradient-to-r from-green-400 to-green-600"
                      style={{ width: '95%' }}
                    ></div>
                  </div>
                  <span className="ml-2 text-xs text-green-600">95%</span>
                </div>
              </div>

              <div className="bg-surface-blue p-3 rounded-qnav border border-primary-100">
                <h3 className="font-medium text-secondary-800 text-sm">Phase 3</h3>
                <div className="flex items-center mt-1.5">
                  <div className="w-full bg-secondary-100 rounded-full h-2 overflow-hidden">
                    <div
                      className="h-2 rounded-full bg-gradient-to-r from-yellow-400 to-yellow-500"
                      style={{ width: '45%' }}
                    ></div>
                  </div>
                  <span className="ml-2 text-xs text-yellow-600">45%</span>
                </div>
              </div>
            </div>
          </div>
          <div className="mt-4 text-center">
            <button className="text-sm text-primary-600 hover:text-primary-800 font-medium hover:underline">
              View Program Timeline
            </button>
          </div>
        </div>
      </div>

      {/* Executive Reports */}
      <div className="bg-surface rounded-qnav shadow-brand-card p-6 border border-secondary-200">
        <h2 className="text-xl font-semibold text-secondary-800 mb-4">Executive Reports</h2>
        <div className="space-y-4">
          <div className="flex items-center justify-between p-4 bg-surface-blue rounded-qnav border border-primary-100">
            <div>
              <h3 className="font-medium text-secondary-800">Weekly Adoption Report</h3>
              <p className="text-sm text-secondary-600">Updated 2 days ago</p>
            </div>
            <button className="qnav-btn-primary text-sm">Generate Report</button>
          </div>

          <div className="flex items-center justify-between p-4 bg-surface-blue rounded-qnav border border-primary-100">
            <div>
              <h3 className="font-medium text-secondary-800">ROI Analysis</h3>
              <p className="text-sm text-secondary-600">Updated 1 week ago</p>
            </div>
            <button className="qnav-btn-primary text-sm">Generate Report</button>
          </div>

          <div className="flex items-center justify-between p-4 bg-surface-blue rounded-qnav border border-primary-100">
            <div>
              <h3 className="font-medium text-secondary-800">Program Completion Forecast</h3>
              <p className="text-sm text-secondary-600">Updated 3 days ago</p>
            </div>
            <button className="qnav-btn-primary text-sm">Generate Report</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export { QNavAnalytics };
